import type { FamilyId, MaterialDependency, ObjectiveType } from '../schema';
import type { SiteThemePresentation } from '../../site/schema';

export type VisualArchetypeRegistry = {
  id: SiteThemePresentation['archetypeId'];
  label: string;
  description: string;
  families: FamilyId[];
  preferredThemeIds: string[];
  objectiveMatches?: ObjectiveType[];
  materialMatches?: MaterialDependency[];
  presentation: SiteThemePresentation;
};

export const VISUAL_ARCHETYPE_REGISTRIES: VisualArchetypeRegistry[] = [
  {
    id: 'jp_b2b_form_focus',
    label: 'BtoB Form Focus',
    description: '資料請求フォームをすぐ見せ、比較検討時の安心感を静かに積むBtoB LP向け。',
    families: ['btob_inquiry'],
    preferredThemeIds: ['theme_trust_neutral', 'theme_corporate_clean', 'theme_formal_trust'],
    objectiveMatches: ['document_request'],
    materialMatches: ['text_first', 'photo_optional', 'photo_required'],
    presentation: {
      archetypeId: 'jp_b2b_form_focus',
      sectionHeadingStyle: 'left_rule',
      cardStyle: 'surface',
      heroLayoutStyle: 'editorial_centered',
      proofLayoutStyle: 'band',
      reviewLayoutStyle: 'editorial',
      ctaLayoutStyle: 'panel',
    },
  },
  {
    id: 'jp_b2b_ui_showcase',
    label: 'BtoB UI Showcase',
    description: 'UIや画面の理解を起点に資料請求へつなぐBtoB SaaS寄りのLP向け。',
    families: ['btob_inquiry'],
    preferredThemeIds: ['theme_corporate_clean', 'theme_modern_trust'],
    objectiveMatches: ['document_request', 'inquiry'],
    materialMatches: ['ui_capture_required'],
    presentation: {
      archetypeId: 'jp_b2b_ui_showcase',
      sectionHeadingStyle: 'left_rule',
      cardStyle: 'outline',
      heroLayoutStyle: 'story_split',
      proofLayoutStyle: 'band',
      reviewLayoutStyle: 'editorial',
      ctaLayoutStyle: 'panel',
    },
  },
  {
    id: 'jp_b2b_trust_authority',
    label: 'BtoB Trust Authority',
    description: '実績・比較・信頼感を落ち着いて見せる日本の法人LP向け。',
    families: ['btob_inquiry'],
    preferredThemeIds: ['theme_trust_neutral', 'theme_corporate_clean', 'theme_formal_trust'],
    objectiveMatches: ['inquiry', 'document_request'],
    presentation: {
      archetypeId: 'jp_b2b_trust_authority',
      sectionHeadingStyle: 'left_rule',
      cardStyle: 'surface',
      heroLayoutStyle: 'authority_band',
      proofLayoutStyle: 'grid',
      reviewLayoutStyle: 'editorial',
      ctaLayoutStyle: 'panel',
    },
  },
  {
    id: 'jp_saas_ui_showcase',
    label: 'SaaS UI Showcase',
    description: 'UI理解と比較しやすさを優先するSaaS系LP向け。',
    families: ['saas_ai_inquiry'],
    preferredThemeIds: ['theme_modern_trust', 'theme_corporate_clean'],
    presentation: {
      archetypeId: 'jp_saas_ui_showcase',
      sectionHeadingStyle: 'left_rule',
      cardStyle: 'outline',
      heroLayoutStyle: 'story_split',
      proofLayoutStyle: 'band',
      reviewLayoutStyle: 'editorial',
      ctaLayoutStyle: 'panel',
    },
  },
  {
    id: 'jp_regional_service_trust',
    label: 'Regional Service Trust',
    description: '緊急対応や相談型の地域サービスで、信頼材料と問い合わせ導線を静かに積むLP向け。',
    families: ['regional_service_inquiry'],
    preferredThemeIds: ['theme_trust_neutral', 'theme_formal_trust', 'theme_regional_chain'],
    objectiveMatches: ['inquiry'],
    presentation: {
      archetypeId: 'jp_regional_service_trust',
      sectionHeadingStyle: 'center_bar',
      cardStyle: 'outline',
      cardShadow: 'medium',
      borderEmphasis: 'soft',
      heroLayoutStyle: 'authority_band',
      proofLayoutStyle: 'band',
      reviewLayoutStyle: 'cards',
      ctaLayoutStyle: 'panel',
    },
  },
  {
    id: 'jp_regional_service_gentle_support',
    label: 'Regional Service Gentle Support',
    description: '寄り添い型の地域サービスで、相談しやすさと地域密着の安心感をやわらかく伝えるLP向け。',
    families: ['regional_service_inquiry'],
    preferredThemeIds: ['theme_natural_warm'],
    objectiveMatches: ['inquiry'],
    presentation: {
      archetypeId: 'jp_regional_service_gentle_support',
      sectionHeadingStyle: 'center_panel',
      cardStyle: 'tint',
      heroLayoutStyle: 'story_split',
      proofLayoutStyle: 'pill',
      reviewLayoutStyle: 'speech',
      ctaLayoutStyle: 'band',
    },
  },
  {
    id: 'jp_local_reassurance',
    label: 'Local Reassurance',
    description: '地域サービスや店舗予約で安心感と親しみを出すLP向け。',
    families: ['regional_service_inquiry', 'store_reservation'],
    preferredThemeIds: ['theme_natural_warm', 'theme_bright_friendly'],
    presentation: {
      archetypeId: 'jp_local_reassurance',
      sectionHeadingStyle: 'center_panel',
      cardStyle: 'tint',
      heroLayoutStyle: 'story_split',
      proofLayoutStyle: 'pill',
      reviewLayoutStyle: 'speech',
      ctaLayoutStyle: 'band',
    },
  },
  {
    id: 'jp_d2c_offer_focus',
    label: 'D2C Offer Focus',
    description: '商品訴求とオファーの強さを前面に出すD2C LP向け。',
    families: ['d2c_purchase'],
    preferredThemeIds: ['theme_premium_minimal', 'theme_bright_friendly'],
    presentation: {
      archetypeId: 'jp_d2c_offer_focus',
      sectionHeadingStyle: 'center_panel',
      cardStyle: 'elevated',
      heroLayoutStyle: 'product_frame',
      proofLayoutStyle: 'pill',
      reviewLayoutStyle: 'cards',
      ctaLayoutStyle: 'contrast',
    },
  },
  {
    id: 'jp_recruit_story_culture',
    label: 'Recruit Story Culture',
    description: 'ミッションや人・働き方を印象づける採用LP向け。',
    families: ['recruitment_entry'],
    preferredThemeIds: ['theme_natural_warm', 'theme_formal_trust', 'theme_corporate_clean'],
    presentation: {
      archetypeId: 'jp_recruit_story_culture',
      sectionHeadingStyle: 'left_rule',
      cardStyle: 'tint',
      heroLayoutStyle: 'story_split',
      proofLayoutStyle: 'grid',
      reviewLayoutStyle: 'speech',
      ctaLayoutStyle: 'band',
    },
  },
  {
    id: 'jp_event_authority_conversion',
    label: 'Event Authority Conversion',
    description: 'セミナーやスクール申込で権威性と申込導線を両立するLP向け。',
    families: ['seminar_application', 'education_application'],
    preferredThemeIds: ['theme_formal_trust', 'theme_trust_neutral', 'theme_modern_trust'],
    presentation: {
      archetypeId: 'jp_event_authority_conversion',
      sectionHeadingStyle: 'center_bar',
      cardStyle: 'surface',
      heroLayoutStyle: 'authority_band',
      proofLayoutStyle: 'band',
      reviewLayoutStyle: 'cards',
      ctaLayoutStyle: 'panel',
    },
  },
] satisfies VisualArchetypeRegistry[];

export const visualArchetypeRegistryMap = new Map<string, VisualArchetypeRegistry>(
  VISUAL_ARCHETYPE_REGISTRIES.map((registry) => [registry.id, registry]),
);

export function getVisualArchetypeRegistry(
  archetypeId: SiteThemePresentation['archetypeId'],
) {
  return visualArchetypeRegistryMap.get(archetypeId);
}

export function resolveVisualArchetypeRegistry(
  familyId?: FamilyId,
  themeId?: string,
  objective?: ObjectiveType,
  materialDependency?: MaterialDependency,
) {
  if (familyId) {
    const familyCandidates = VISUAL_ARCHETYPE_REGISTRIES.filter((registry) =>
      registry.families.includes(familyId),
    );

    const exactThemeMatch = familyCandidates.find((registry) =>
      (!themeId || registry.preferredThemeIds.includes(themeId)) &&
      (!registry.objectiveMatches || (objective ? registry.objectiveMatches.includes(objective) : false)) &&
      (!registry.materialMatches || (materialDependency ? registry.materialMatches.includes(materialDependency) : false)),
    );

    if (exactThemeMatch) {
      return exactThemeMatch;
    }

    const exactMatch = familyCandidates.find((registry) =>
      (!registry.objectiveMatches || (objective ? registry.objectiveMatches.includes(objective) : false)) &&
      (!registry.materialMatches || (materialDependency ? registry.materialMatches.includes(materialDependency) : false)),
    );

    if (exactMatch) {
      return exactMatch;
    }

    const objectiveThemeMatch = familyCandidates.find((registry) =>
      (!themeId || registry.preferredThemeIds.includes(themeId)) &&
      !registry.materialMatches &&
      (!registry.objectiveMatches || (objective ? registry.objectiveMatches.includes(objective) : false)),
    );

    if (objectiveThemeMatch) {
      return objectiveThemeMatch;
    }

    const objectiveMatch = familyCandidates.find((registry) =>
      !registry.materialMatches &&
      (!registry.objectiveMatches || (objective ? registry.objectiveMatches.includes(objective) : false)),
    );

    if (objectiveMatch) {
      return objectiveMatch;
    }

    if (themeId) {
      const familyThemeMatch = familyCandidates.find((registry) =>
        registry.preferredThemeIds.includes(themeId),
      );

      if (familyThemeMatch) {
        return familyThemeMatch;
      }
    }

    if (familyCandidates[0]) {
      return familyCandidates[0];
    }
  }

  if (themeId) {
    const themeMatch = VISUAL_ARCHETYPE_REGISTRIES.find((registry) =>
      registry.preferredThemeIds.includes(themeId),
    );

    if (themeMatch) {
      return themeMatch;
    }
  }

  return VISUAL_ARCHETYPE_REGISTRIES[0];
}
